import React, { useEffect, useRef, useState } from "react"
import { Platform } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import { AppModule } from "@treefort/api-spec"
import { IN_PROGRESS_SLIDER_CONTENT_TYPES } from "@treefort/constants"
import { useAuth } from "@treefort/lib/auth-provider"
import {
  Result,
  ResultAsync,
  ResultLoading,
  ResultSuccess,
} from "@treefort/lib/result"

import { ListViewRef } from "../../../components/list-view"
import Spacer from "../../../components/spacer"
import { useTokens } from "../../../components/tokens-provider"
import { useProgressItemCountsByContentType } from "../../../hooks/use-progress"
import { kebabCase } from "../../../lib/kebab-case"
import PageLayout from "../page"
import {
  ItemArgs,
  ListViewModule,
  ModuleVisibilityInfo,
} from "./list-view-module"

export const SLIDER_PROGRESS_LIMIT = 50

type ListViewItem = {
  listViewModule: ListViewModule<AppModule, unknown>
  itemArgs: ItemArgs<unknown>
}

/**
 * Takes in two list view items and, if the items represent a break between
 * modules, returns the largest gapSize configured by the two modules.
 */
const getGapSize = (itemBefore: ListViewItem, itemAfter: ListViewItem) =>
  itemBefore.listViewModule === itemAfter.listViewModule
    ? 0
    : Math.max(
        itemBefore.listViewModule.gapSize,
        itemAfter.listViewModule.gapSize,
      )

export function ModuleLayout({
  pageKey,
  listViewModules,
  children,
}: {
  // A key that uniquely identifies the layout in the app
  pageKey: string
  listViewModules: ResultAsync<ListViewModule<AppModule, unknown>[]>
  children?: (props: {
    listView: React.RefObject<ListViewRef<ListViewItem> | null>
  }) => React.JSX.Element | null
}): React.JSX.Element {
  const auth = useAuth()
  const listView = useRef<ListViewRef<ListViewItem>>(null)
  const { tokens, displayHeight, displayWidth } = useTokens()
  const safeAreaInsets = useSafeAreaInsets()
  const [pageLayoutKey, setPageLayoutKey] = useState(0)

  const inProgressCountsEnabled = listViewModules.data?.some((module) =>
    module.visibility?.dependencies.includes("inProgressCounts"),
  )

  const inProgressCounts = useProgressItemCountsByContentType({
    limit: SLIDER_PROGRESS_LIMIT,
    filterBy: (progressItem) =>
      progressItem.inProgress() &&
      IN_PROGRESS_SLIDER_CONTENT_TYPES.some(
        (contentType) => contentType === progressItem.getData().contentType,
      ),
    enabled: inProgressCountsEnabled,
  })

  const visibilityInfo: ModuleVisibilityInfo = {
    userId:
      auth.initialized && !auth.loading
        ? new ResultSuccess(auth.user?.id)
        : new ResultLoading(),
    inProgressCounts: inProgressCounts
      ? new ResultSuccess(inProgressCounts)
      : new ResultLoading(),
  }

  const visibleListViewModules = listViewModules.data?.filter(
    (listViewModule) =>
      !listViewModule.visibility ||
      !listViewModule.visibility.isHidden(visibilityInfo),
  )

  // The max width of the layout is the largest max width of any individual
  // module
  const maxWidth = visibleListViewModules
    ?.map((listViewModule) => listViewModule.maxWidth)
    .reduce(
      (result, maxWidth) =>
        result !== undefined && maxWidth !== undefined
          ? Math.min(result, maxWidth)
          : result || maxWidth,
      undefined,
    )

  const safeAreaInsetTop =
    safeAreaInsets.top +
    (visibleListViewModules?.[0]?.appHeaderState === "collapsed"
      ? tokens.appHeader.minHeight
      : tokens.appHeader.maxHeight)
  const safeAreaInsetsHeadItem = {
    ...safeAreaInsets,
    top: safeAreaInsetTop,
    bottom: 0,
  }
  const safeAreaInsetsTailItem = { ...safeAreaInsets, top: 0, bottom: 0 }

  const virtualizeable = visibleListViewModules?.every(
    (listViewModule) => listViewModule.getItemSize !== undefined,
  )

  const listViewItems = Result.mapSuccess(listViewModules, (listViewModules) =>
    Result.success(
      listViewModules
        .filter(
          (listViewModule) =>
            !listViewModule.visibility ||
            !listViewModule.visibility.isHidden(visibilityInfo),
        )
        .flatMap((listViewModule) =>
          listViewModule.getItems({ tokens }).map((item, indexInModule) => ({
            item,
            indexInModule,
            listViewModule,
          })),
        )
        .map(({ item, indexInModule, listViewModule }, indexInPage, items) => ({
          listViewModule,
          itemArgs: {
            item,
            indexInModule,
            indexInPage,
            pageKey,
            tokens,
            displayHeight,
            displayWidth,
            maxWidth,
            safeAreaInsets:
              indexInPage === 0
                ? safeAreaInsetsHeadItem
                : safeAreaInsetsTailItem,
            itemCount: items.length,
          },
        })),
    ),
  )

  const paddingEnd =
    listViewItems.data?.[listViewItems.data.length - 1]?.listViewModule
      .gapSize || 0

  const prevVisibleListViewModulesCount = useRef<number>(undefined)
  const isInfoLoading = Object.values(visibilityInfo).some(
    (value) => value.status === "loading",
  )

  // Re-render the page if fewer modules are visible than before to prevent a
  // potential gap at the end of the page. The most likely cause of this would
  // be the user switching to a new profile. On the web changing pages resets
  // scroll position, so this is only necessary on native.
  useEffect(() => {
    if (isInfoLoading || Platform.OS === "web") {
      return
    }

    if (
      typeof prevVisibleListViewModulesCount.current === "number" &&
      typeof visibleListViewModules?.length === "number" &&
      visibleListViewModules.length < prevVisibleListViewModulesCount.current
    ) {
      setPageLayoutKey((key) => key + 1)
    }

    prevVisibleListViewModulesCount.current = visibleListViewModules?.length
  }, [isInfoLoading, visibleListViewModules?.length])

  return (
    <PageLayout
      key={pageLayoutKey}
      listViewProps={
        listViewItems.isSuccess && virtualizeable
          ? {
              id: kebabCase(`module-layout-${pageKey}`),
              ref: listView,
              renderItem: ({ listViewModule, itemArgs }) =>
                listViewModule.renderItem(itemArgs),
              getItemSize: ({ listViewModule, itemArgs }) =>
                listViewModule.getItemSize?.(itemArgs) || 0,
              getItemKey: ({ listViewModule, itemArgs }) =>
                listViewModule.getItemKey(itemArgs),
              items: listViewItems.data,
              getGapSize,
              paddingEnd,
              scrollBehavior: "smooth",
            }
          : undefined
      }
      headerPresentation={visibleListViewModules?.[0]?.appHeaderPresentation}
      headerState={visibleListViewModules?.[0]?.appHeaderState}
      hideDesktopFooter={visibleListViewModules?.[0]?.hideDesktopFooter}
    >
      {listViewItems.isSuccess && !virtualizeable
        ? listViewItems.data.map((item) => {
            const { listViewModule, itemArgs } = item
            const itemBefore = listViewItems.data[itemArgs.indexInPage - 1]
            const gap = itemBefore ? getGapSize(itemBefore, item) : 0
            return (
              <React.Fragment key={listViewModule.getItemKey(itemArgs)}>
                {gap ? <Spacer size={gap} /> : null}
                {listViewModule.renderItem(itemArgs)}
              </React.Fragment>
            )
          })
        : null}
      {paddingEnd && !virtualizeable ? <Spacer size={paddingEnd} /> : null}
      {children?.({ listView })}
    </PageLayout>
  )
}
